/**
 * Scheduling — single source of truth for time-slot definitions, round caps,
 * and proposal limits used across the web app.
 *
 * IMPORTANT: This file is mirrored by
 * `supabase/functions/_shared/scheduling-slots.ts`. The two MUST stay in sync.
 * If you change a value here, update the mirror and the corresponding DB
 * CHECK constraint / enum.
 */

export type TimeSlotValue = 'MORNING' | 'AFTERNOON' | 'EVENING' | 'FULL_DAY';

export interface TimeSlotDefinition {
    value: TimeSlotValue;
    /** Long label for menus, e.g. "Morning (8 am – 12 pm)" */
    label: string;
    /** Short label for chips/toggles, e.g. "Morning" */
    shortLabel: string;
    /** Window start hour (0–23) in the job's local timezone */
    startHour: number;
    /** Window end hour (0–23) in the job's local timezone */
    endHour: number;
}

/**
 * Canonical time-slot definitions.
 *
 * Hours match the trade industry convention used by the platform:
 *   Morning   08:00–12:00
 *   Afternoon 12:00–17:00
 *   Evening   17:00–20:00
 *   Full Day  08:00–17:00
 *
 * Do not branch these for different surfaces — if a surface needs different
 * hours, it's actually a different concept.
 */
export const TIME_SLOTS: ReadonlyArray<TimeSlotDefinition> = [
    {
        value: 'MORNING',
        label: 'Morning (8 am – 12 pm)',
        shortLabel: 'Morning',
        startHour: 8,
        endHour: 12,
    },
    {
        value: 'AFTERNOON',
        label: 'Afternoon (12 pm – 5 pm)',
        shortLabel: 'Afternoon',
        startHour: 12,
        endHour: 17,
    },
    {
        value: 'EVENING',
        label: 'Evening (5 pm – 8 pm)',
        shortLabel: 'Evening',
        startHour: 17,
        endHour: 20,
    },
    {
        value: 'FULL_DAY',
        label: 'Full Day (8 am – 5 pm)',
        shortLabel: 'Full Day',
        startHour: 8,
        endHour: 17,
    },
] as const;

const SLOT_BY_VALUE = new Map<string, TimeSlotDefinition>(
    TIME_SLOTS.map((s) => [s.value, s]),
);

/**
 * Look up a slot definition by value. Accepts legacy lowercase values
 * (`morning`, `afternoon`, `evening`) and normalises them to uppercase so
 * data from older `job_reschedule_requests` rows still renders correctly.
 *
 * Returns undefined for unknown / empty values — callers should treat that as
 * "no slot specified" (the customer is flexible).
 */
export function getSlot(value: string | null | undefined): TimeSlotDefinition | undefined {
    if (!value) return undefined;
    return SLOT_BY_VALUE.get(value.toUpperCase());
}

/**
 * Display label for a slot value, falling back gracefully for unknown values.
 *
 * @param value The slot value (canonical or legacy lowercase)
 * @param variant 'long' (default) returns "Morning (8 am – 12 pm)";
 *                'short' returns "Morning"
 */
export function getSlotLabel(
    value: string | null | undefined,
    variant: 'long' | 'short' = 'long',
): string {
    const slot = getSlot(value);
    if (!slot) return value ?? '';
    return variant === 'short' ? slot.shortLabel : slot.label;
}

/** The halves of a day, in the order a second option on the same date is offered. */
const HALF_DAY_SLOTS: ReadonlyArray<TimeSlotValue> = ['MORNING', 'AFTERNOON'];

/**
 * Whether two slots on the same date overlap. A missing or unknown slot counts
 * as Full Day, the slot an acceptance falls back to. Morning and Afternoon do
 * not overlap; Full Day overlaps both, and a slot always overlaps itself.
 *
 * A proposal's options are unique by date + slot under this rule: Monday
 * Morning and Monday Afternoon are two options, Monday Full Day and Monday
 * Morning are refused as overlapping.
 */
export function slotsOverlap(
    a: string | null | undefined,
    b: string | null | undefined,
): boolean {
    const sa = getSlot(a) ?? getSlot('FULL_DAY');
    const sb = getSlot(b) ?? getSlot('FULL_DAY');
    if (!sa || !sb) return true;
    return sa.startHour < sb.endHour && sb.startHour < sa.endHour;
}

/**
 * The slot to add when a date already in a proposal is offered again: the
 * first half of the day not yet covered. A day offered as Full Day is split,
 * so `splitFullDay` tells the caller to turn that option into Morning while
 * Afternoon is added. Null when the day already holds both halves.
 */
export function nextSameDaySlot(
    takenSlots: ReadonlyArray<string | null | undefined>,
): { slot: TimeSlotValue; splitFullDay: boolean } | null {
    for (const slot of HALF_DAY_SLOTS) {
        if (!takenSlots.some((taken) => slotsOverlap(taken, slot))) {
            return { slot, splitFullDay: false };
        }
    }
    if (takenSlots.length === 1 && (getSlot(takenSlots[0])?.value ?? 'FULL_DAY') === 'FULL_DAY') {
        return { slot: 'AFTERNOON', splitFullDay: true };
    }
    return null;
}

/**
 * Whether an option is the one that was accepted. The date alone identifies it unless the day was
 * offered twice (Morning and Afternoon); then the accepted start time picks the half: before noon
 * is Morning, noon or later is Afternoon, and no time is Full Day.
 */
export function isAcceptedOption(
    option: { date: string; time_slot?: string | null },
    options: ReadonlyArray<{ date: string; time_slot?: string | null }>,
    selectedDate: string | null | undefined,
    selectedTime: string | null | undefined,
): boolean {
    if (!selectedDate || option.date !== selectedDate) return false;
    if (options.filter((o) => o.date === selectedDate).length < 2) return true;
    const hour = selectedTime ? Number.parseInt(selectedTime.slice(0, 2), 10) : NaN;
    const acceptedSlot = Number.isNaN(hour) ? 'FULL_DAY' : hour < 12 ? 'MORNING' : 'AFTERNOON';
    return (getSlot(option.time_slot)?.value ?? 'FULL_DAY') === acceptedSlot;
}

/**
 * The first app version that accepts a proposed option by its slot (card 22b09173). Builds up to
 * 1.1.60 accept by date alone and would book the first option on a date, so a day offered as
 * Morning and Afternoon stays locked until every platform's `app_versions.min_supported_version`
 * reaches this version (the forced update). Mirrored in `supabase/functions/_shared/scheduling-slots.ts`.
 */
export const SAME_DAY_SLOTS_MIN_APP_VERSION = '1.1.61';

/** Shown when a proposal offers a date twice while same-day options are locked. */
export const SAME_DAY_SLOTS_LOCKED_MESSAGE =
    'Each date can be offered once for now. Morning and afternoon on the same day will be available once everyone has the latest app.';

const parseAppVersion = (value: unknown): number[] | null => {
    if (typeof value !== 'string') return null;
    const parts = value.trim().split('.');
    if (parts.some((part) => !/^\d+$/.test(part))) return null;
    return parts.map((part) => Number.parseInt(part, 10));
};

/**
 * Compares two dotted numeric versions ('1.1.61'): negative, zero or positive like a sort
 * comparator, with missing parts read as 0 ('1.2' equals '1.2.0'). Null when either is not a
 * plain dotted number ('v1.2', '1.2-beta', '', null).
 */
export function compareAppVersions(a: unknown, b: unknown): number | null {
    const pa = parseAppVersion(a);
    const pb = parseAppVersion(b);
    if (!pa || !pb) return null;
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
        if (diff !== 0) return diff < 0 ? -1 : 1;
    }
    return 0;
}

/**
 * Whether a day may be offered as Morning and Afternoon: only when `app_versions` has at least
 * one row and every row's `min_supported_version` is at least SAME_DAY_SLOTS_MIN_APP_VERSION.
 * No rows, a missing row list (read error) or an unparsable version keeps it locked.
 */
export function sameDaySlotsUnlocked(
    rows: ReadonlyArray<{ min_supported_version?: unknown } | null | undefined> | null | undefined,
): boolean {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    return rows.every((row) => {
        const cmp = compareAppVersions(row?.min_supported_version, SAME_DAY_SLOTS_MIN_APP_VERSION);
        return cmp !== null && cmp >= 0;
    });
}

/** Maximum dates a single proposal may contain. */
export const MAX_PROPOSED_DATES = 3;

/** Maximum counter-proposal rounds before a deadlock state is forced. */
export const MAX_PROPOSAL_ROUNDS = 3;

/** Hours after which a pending proposal / reschedule request expires. */
export const PROPOSAL_EXPIRY_HOURS = 72;

/** Threshold (hours before scheduled date) at which a reschedule incurs a late fee. */
export const LATE_RESCHEDULE_THRESHOLD_HOURS = 24;
